import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ApplicationService } from 'src/app/core/services/application.service';
import { environment } from 'src/environments/environment';
import { AUDIT_COMPONENT_NAME, AUDIT_DETAILS } from './audit-context';

/**
 * HTTP interceptor that attaches an `audit-context` header to every outgoing
 * request targeting the API host (`environment.api`). The header value is a
 * URI-encoded JSON object containing:
 *
 * - `component_name` — the front-end screen that initiated the request.
 *   Defaults automatically to the current route pattern (e.g.
 *   `dashboard/children/details/:id`); an explicit `x-audit-component-name`
 *   header overrides the default.
 * - `details` — a human-readable description of the request purpose.
 *   Defaults automatically to `<METHOD> <api path>` (query strings are
 *   dropped); an explicit `x-audit-details` header overrides the default.
 * - `centre_location_id` — the currently selected centre location ID
 *   (automatically pulled from ApplicationService)
 *
 * The defaults are automatic — feature code does not need to set anything.
 * Requests to non-API hosts (e.g. MS login) are passed through untouched.
 * The temporary `x-audit-*` headers are stripped before the request is sent
 * to the server.
 *
 * ### Usage in feature services (optional override)
 *
 * ```ts
 * import { AUDIT_COMPONENT_NAME, AUDIT_DETAILS } from 'src/app/core/interceptors/audit-context';
 *
 * // When calling GlobalDataStore methods that accept headers:
 * this.splitBillingService.createRecord(data, [], {
 *   [AUDIT_COMPONENT_NAME]: 'SplitBillingComponent',
 *   [AUDIT_DETAILS]: 'Creating billing contact for child',
 * });
 * ```
 *
 * The `centre_location_id` is always included automatically — no need to set
 * it per-request.
 */
@Injectable()
export class AuditContextInterceptor implements HttpInterceptor {
  constructor(
    private injector: Injector,
    private appService: ApplicationService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Only API requests carry the audit context; third-party requests
    // (e.g. MS login) must not receive it.
    if (!request.url.startsWith(environment.api)) {
      return next.handle(request);
    }

    const componentName = (
      request.headers.get(AUDIT_COMPONENT_NAME) || this.routePattern()
    ).slice(0, 255);
    const details = (
      request.headers.get(AUDIT_DETAILS) ||
      `${request.method} ${this.apiPath(request)}`
    ).slice(0, 2000);

    const auditContext = encodeURIComponent(
      JSON.stringify({
        component_name: componentName,
        details: details,
        centre_location_id: this.appService.app?.centerLocation?.id ?? '',
      }),
    );

    // Clone the request: add the audit-context header and strip the temporary
    // x-audit-* headers so they are never sent to the backend server.
    const clonedRequest = request.clone({
      setHeaders: {
        'audit-context': auditContext,
      },
    });

    // Remove the temporary audit headers that were used for transport
    const cleanedRequest = clonedRequest.clone({
      headers: clonedRequest.headers
        .delete(AUDIT_COMPONENT_NAME)
        .delete(AUDIT_DETAILS),
    });

    return next.handle(cleanedRequest);
  }

  /**
   * Default `component_name`: the current route pattern, built from the
   * activated route config paths (e.g. `dashboard/children/details/:id`).
   * Falls back to `app` when no route paths are available.
   */
  private routePattern(): string {
    const router = this.injector.get(Router);
    const segments: string[] = [];
    let route = router.routerState.snapshot.root.firstChild;
    while (route) {
      const path = route.routeConfig?.path;
      if (path) {
        segments.push(path);
      }
      route = route.firstChild;
    }
    return segments.join('/') || 'app';
  }

  /**
   * Default `details`: the request path relative to the API, without the
   * query string (queries can hold PII).
   */
  private apiPath(req: HttpRequest<any>): string {
    return req.url
      .slice(environment.api.length)
      .split('?')[0]
      .replace(/^\/+/, '');
  }
}
